import { createSlice, EntityId, EntityState, PayloadAction } from '@reduxjs/toolkit';
import { ListObjectWithStates } from 'app/main/design-system/models/listObjectsModel';
import { GameActions, GameInDTO, GameInfoDTO, GameListItemDTO, GameOutDTO, GameRewardsDTO } from '../types';
import { gameRewardsAdapter, gamesAdapter } from './games.adapter';
import {
  createNewGameThunk,
  fetchGamesThunk,
  getGameInfoThunk,
  getGameRewardsThunk,
  updateGameStatusThunk,
} from './games.thunk';

export interface GameForm extends Partial<GameInDTO> {
  rewardEntities: EntityState<GameRewardsDTO>;
}

interface GameList
  extends EntityState<GameListItemDTO>,
    Pick<ListObjectWithStates<GameListItemDTO>, 'hasError' | 'isLoading' | 'metadata'> {}

export interface GamesState {
  list: GameList;
  current: {
    isLoading: boolean;
    hasBeenSaved: boolean;
    hasError: boolean;
    form: GameForm;
    isStepValid: boolean;
  };
  selected: {
    gameInfo: GameInfoDTO | undefined;
    rewards: GameRewardsDTO[] | undefined;
    isLoading: boolean;
    hasError: boolean;
    isStatusLoading: boolean;
    hasStatusError: boolean;
  };
}

const initialState: GamesState = {
  list: gamesAdapter.getInitialState({
    metadata: {
      page: 0,
      size: 0,
      total: 0,
    },
    isLoading: false,
    hasError: false,
  }),
  current: {
    isLoading: false,
    hasBeenSaved: false,
    hasError: false,
    form: {
      rewardEntities: gameRewardsAdapter.getInitialState(),
      pageHeader: "En échange d'un avis Google",
      action: GameActions.GOOGLE,
    },
    isStepValid: false,
  },
  selected: {
    gameInfo: undefined,
    rewards: undefined,
    isLoading: false,
    hasError: false,
    isStatusLoading: false,
    hasStatusError: false,
  },
};

export const { actions, reducer } = createSlice({
  name: 'games',
  initialState,
  reducers: {
    updateCurrentGame: (state, action: PayloadAction<Partial<GameOutDTO>>) => {
      state.current.form = {
        ...state.current.form,
        ...action.payload,
      };
    },
    updateCurrentGameAction: (state, action: PayloadAction<GameActions>) => {
      state.current.form.action = action.payload;
      state.current.form.storeInfos = (state.current.form.storeInfos || []).map(info => ({
        ...info,
        link: action.payload === GameActions.OPENING ? undefined : info.link,
        action: action.payload,
      }));
    },
    resetCurrentForm: state => {
      state.current.form = initialState.current.form;
    },
    updateCurrentGameReward: (state, action: PayloadAction<{ id: EntityId; data: Partial<GameRewardsDTO> }>) => {
      gameRewardsAdapter.updateOne(state.current.form.rewardEntities, {
        id: action.payload.id,
        changes: {
          ...action.payload.data,
        },
      });
    },
    setValidForm: (state, action: PayloadAction<boolean>) => {
      state.current.isStepValid = action.payload;
    },
    addGameReward: (state, action: PayloadAction<GameRewardsDTO>) => {
      gameRewardsAdapter.addOne(state.current.form.rewardEntities, action.payload);
    },
    deleteGameReward: (state, action: PayloadAction<EntityId>) => {
      gameRewardsAdapter.removeOne(state.current.form.rewardEntities, action.payload);
    },
    updateGame: (state, action: PayloadAction<GameInDTO>) => {
      const { id, ...game } = action.payload;
      gamesAdapter.updateOne(state.list, {
        id,
        changes: {
          ...game,
        },
      });
    },
    setGameValidationDialog: (state, action: PayloadAction<{ isLoading?: boolean; hasBeenSaved?: boolean }>) => {
      state.current = {
        ...state.current,
        ...action.payload,
      };
    },
  },
  extraReducers: builder => {
    builder.addCase(createNewGameThunk.pending, state => {
      state.current.isLoading = true;
      state.current.hasError = false;
    });
    builder.addCase(createNewGameThunk.fulfilled, (state, action) => {
      const { id, ...game } = action.payload;
      gamesAdapter.updateOne(state.list, {
        id,
        changes: {
          ...game,
        },
      });
      state.current.isLoading = false;
      state.current.hasBeenSaved = true;
      state.current.hasError = false;
      state.current.form = initialState.current.form;
    });
    builder.addCase(createNewGameThunk.rejected, state => {
      state.current.isLoading = false;
      state.current.hasError = true;
    });
    builder.addCase(fetchGamesThunk.fulfilled, (state, action) => {
      const { data, metadata } = action.payload;
      gamesAdapter.setAll(state.list, data);
      state.list = {
        ...state.list,
        metadata,
        isLoading: false,
        hasError: false,
      };
    });
    builder.addCase(fetchGamesThunk.pending, state => {
      state.list.isLoading = true;
      state.list.hasError = false;
    });
    builder.addCase(fetchGamesThunk.rejected, state => {
      gamesAdapter.removeAll(state.list);
      state.list.isLoading = false;
      state.list.hasError = true;
    });
    builder.addCase(getGameInfoThunk.fulfilled, (state, action) => {
      const { id, ...game } = action.payload;
      state.selected.gameInfo = action.payload;
      gamesAdapter.updateOne(state.list, {
        id,
        changes: {
          ...game,
        },
      });
      state.selected.isLoading = false;
      state.selected.hasError = false;
    });
    builder.addCase(getGameInfoThunk.pending, state => {
      state.selected.isLoading = true;
      state.selected.hasError = false;
    });
    builder.addCase(getGameInfoThunk.rejected, state => {
      state.selected.isLoading = false;
      state.selected.hasError = true;
    });
    builder.addCase(getGameRewardsThunk.fulfilled, (state, action) => {
      state.selected.rewards = action.payload;
      state.selected.isLoading = false;
      state.selected.hasError = false;
    });
    builder.addCase(getGameRewardsThunk.pending, state => {
      state.selected.isLoading = true;
      state.selected.hasError = false;
    });
    builder.addCase(getGameRewardsThunk.rejected, state => {
      state.selected.isLoading = false;
      state.selected.hasError = true;
    });
    builder.addCase(updateGameStatusThunk.fulfilled, (state, action) => {
      const { id, disabledAt } = action.payload;
      gamesAdapter.updateOne(state.list, {
        id,
        changes: {
          disabledAt,
        },
      });
      if (state.selected.gameInfo) {
        state.selected.gameInfo = {
          ...state.selected.gameInfo,
          disabledAt,
        };
      }
      state.selected.isStatusLoading = false;
      state.selected.hasStatusError = false;
    });
    builder.addCase(updateGameStatusThunk.pending, state => {
      state.selected.isStatusLoading = true;
      state.selected.hasStatusError = false;
    });
    builder.addCase(updateGameStatusThunk.rejected, state => {
      state.selected.isStatusLoading = false;
      state.selected.hasStatusError = true;
    });
  },
});

export const {
  updateCurrentGame,
  updateCurrentGameAction,
  updateCurrentGameReward,
  addGameReward,
  setValidForm,
  updateGame,
  deleteGameReward,
  setGameValidationDialog,
  resetCurrentForm,
} = actions;
